import { useEffect, useState } from 'react';
import { useT } from '../i18n';

/**
 * Escolha de um template aprovado da Meta para uma mensagem de campanha.
 *
 * Os templates são da conta WhatsApp Business (WABA) da conexão: buscamos da primeira conexão
 * Cloud API selecionada na campanha. Os valores dos parâmetros aceitam as variáveis da campanha
 * ({{name}}, {{phone}}...), trocadas por contato no envio.
 */

export interface TemplateContent {
  templateName: string;
  language: string;
  parameterFormat?: 'POSITIONAL' | 'NAMED';
  headerMediaType?: 'IMAGE' | 'VIDEO' | 'DOCUMENT' | null;
  headerMediaUrl?: string;
  headerParams?: { name: string; value: string }[];
  bodyParams?: { name: string; value: string }[];
  buttonParams?: { index: number; type: 'URL' | 'COPY_CODE'; value: string }[];
  preview?: string;
}

interface TemplateSummary {
  name: string;
  language: string;
  status: string;
  category: string;
  parameterFormat: 'POSITIONAL' | 'NAMED';
  headerFormat: 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT' | 'LOCATION' | null;
  headerText: string | null;
  bodyText: string;
  footerText: string | null;
  headerParams: string[];
  bodyParams: string[];
  buttons: { type: string; text: string; url?: string; needsValue: boolean }[];
}

const MEDIA_HEADERS = ['IMAGE', 'VIDEO', 'DOCUMENT'];
const keyOf = (t: { name: string; language: string }) => `${t.name}|${t.language}`;

export function MetaTemplatePicker({ sessionName, value, onChange }: {
  sessionName: string | null;
  value: TemplateContent;
  onChange: (content: TemplateContent) => void;
}) {
  const t = useT();
  const [templates, setTemplates] = useState<TemplateSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!sessionName) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetch(`/api/waha/sessions/${encodeURIComponent(sessionName)}/meta-templates`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}` },
    })
      .then(async response => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error || `HTTP ${response.status}`);
        if (!cancelled) setTemplates(data.templates || []);
      })
      .catch(err => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [sessionName]);

  if (!sessionName) {
    return <p className="text-sm text-amber-700">{t('camp.templateNeedsMeta')}</p>;
  }

  const approved = templates.filter(tpl => tpl.status === 'APPROVED');
  const pending = templates.length - approved.length;
  const selected = templates.find(tpl => value.templateName && keyOf(tpl) === `${value.templateName}|${value.language}`);

  const choose = (key: string) => {
    const tpl = templates.find(item => keyOf(item) === key);
    if (!tpl) {
      onChange({ templateName: '', language: '' });
      return;
    }
    onChange({
      templateName: tpl.name,
      language: tpl.language,
      parameterFormat: tpl.parameterFormat,
      headerMediaType: tpl.headerFormat && MEDIA_HEADERS.includes(tpl.headerFormat)
        ? tpl.headerFormat as TemplateContent['headerMediaType'] : null,
      headerMediaUrl: '',
      headerParams: tpl.headerParams.map(name => ({ name, value: '' })),
      bodyParams: tpl.bodyParams.map(name => ({ name, value: '' })),
      buttonParams: tpl.buttons
        .map((button, index) => ({ button, index }))
        .filter(({ button }) => button.needsValue)
        .map(({ button, index }) => ({ index, type: button.type as 'URL' | 'COPY_CODE', value: '' })),
      preview: [tpl.headerText, tpl.bodyText, tpl.footerText].filter(Boolean).join('\n\n'),
    });
  };

  // Mesmo upload das mídias da campanha: o arquivo fica na aplicação e a Meta baixa pelo link
  const uploadHeader = async (file: File) => {
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const response = await fetch('/api/media/upload', {
        method: 'POST',
        body: form,
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}` },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.fileUrl) throw new Error(data.error || t('camp.templateUploadFailed'));
      onChange({ ...value, headerMediaUrl: data.fileUrl });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const accept = value.headerMediaType === 'IMAGE' ? 'image/jpeg,image/png'
    : value.headerMediaType === 'VIDEO' ? 'video/mp4' : '.pdf,application/pdf';

  const setParam = (list: 'headerParams' | 'bodyParams', index: number, paramValue: string) => {
    const params = [...(value[list] || [])];
    params[index] = { ...params[index], value: paramValue };
    onChange({ ...value, [list]: params });
  };

  const setButtonParam = (index: number, buttonValue: string) => {
    onChange({
      ...value,
      buttonParams: (value.buttonParams || []).map(p => (p.index === index ? { ...p, value: buttonValue } : p)),
    });
  };

  const paramInputs = (list: 'headerParams' | 'bodyParams') => (value[list] || []).map((param, index) => (
    <div key={`${list}-${param.name}`} className="flex items-center gap-2">
      <code dir="ltr" className="text-xs bg-gray-100 px-2 py-1 rounded shrink-0">{`{{${param.name}}}`}</code>
      <input
        type="text"
        value={param.value}
        onChange={(e) => setParam(list, index, e.target.value)}
        placeholder={t('camp.templateParamPlaceholder')}
        className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
    </div>
  ));

  return (
    <div className="space-y-3">
      {loading && <p className="text-sm text-gray-500">{t('common.loading')}</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {!loading && !error && (
        <>
          <select
            value={selected ? keyOf(selected) : ''}
            onChange={(e) => choose(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">{approved.length ? t('camp.templateChoose') : t('camp.templateNone')}</option>
            {approved.map(tpl => (
              <option key={keyOf(tpl)} value={keyOf(tpl)}>{tpl.name} ({tpl.language}) · {tpl.category}</option>
            ))}
          </select>
          {pending > 0 && <p className="text-xs text-gray-500">{t('camp.templatePending').replace('{n}', String(pending))}</p>}
          {value.templateName && !selected && (
            <p className="text-xs text-amber-700">{t('camp.templateMissing').replace('{name}', value.templateName)}</p>
          )}
        </>
      )}

      {value.templateName && (
        <>
          {value.preview && (
            <div dir="auto" className="whitespace-pre-wrap text-sm bg-emerald-50 border border-emerald-200 rounded-md p-3">
              {value.preview}
              {selected && selected.buttons.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {selected.buttons.map(button => (
                    <span key={button.text} className="text-xs bg-white border border-emerald-300 rounded px-2 py-1">{button.text}</span>
                  ))}
                </div>
              )}
            </div>
          )}

          {value.headerMediaType && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('camp.templateHeaderMedia').replace('{type}', value.headerMediaType.toLowerCase())}
              </label>
              <div className="flex gap-2">
                <input
                  type="url"
                  dir="ltr"
                  value={value.headerMediaUrl || ''}
                  onChange={(e) => onChange({ ...value, headerMediaUrl: e.target.value })}
                  placeholder="https://"
                  className={`flex-1 min-w-0 px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    value.headerMediaUrl ? 'border-gray-300' : 'border-red-400'
                  }`}
                />
                <label className={`px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-sm cursor-pointer hover:bg-gray-200 shrink-0 ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
                  {uploading ? t('camp.templateUploading') : t('camp.templateUpload')}
                  <input
                    type="file"
                    accept={accept}
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) uploadHeader(file);
                      e.target.value = '';
                    }}
                  />
                </label>
              </div>
              {!value.headerMediaUrl && <p className="text-xs text-red-600 mt-1">{t('camp.templateHeaderRequired')}</p>}
              {value.headerMediaType === 'IMAGE' && value.headerMediaUrl && (
                <img src={value.headerMediaUrl} alt="" className="mt-2 max-h-32 rounded border border-gray-200" />
              )}
            </div>
          )}

          {((value.headerParams?.length || 0) + (value.bodyParams?.length || 0)) > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">{t('camp.templateParams')}</p>
              {paramInputs('headerParams')}
              {paramInputs('bodyParams')}
            </div>
          )}

          {(value.buttonParams?.length || 0) > 0 && (
            <div className="space-y-2">
              {(value.buttonParams || []).map(param => {
                const button = selected?.buttons[param.index];
                const isCoupon = param.type === 'COPY_CODE';
                return (
                  <div key={`button-${param.index}`}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {isCoupon ? t('camp.templateCoupon') : t('camp.templateUrlSuffix').replace('{button}', button?.text || '')}
                    </label>
                    {!isCoupon && button?.url && <p dir="ltr" className="text-xs text-gray-500 mb-1">{button.url}</p>}
                    <input
                      type="text"
                      dir="ltr"
                      value={param.value}
                      onChange={(e) => setButtonParam(param.index, e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        isCoupon && !param.value.trim() ? 'border-red-400' : 'border-gray-300'
                      }`}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
